// Painel de balanceamento (só administrador): XP e gold por hora de cada hunt, gold que entra e sai da economia,
// bosses (entradas x vitórias) e mortes. Os números vêm dos contadores diários do servidor.
import { useEffect, useState } from 'preact/hooks';
import { session } from '../../api';

interface Data { days: number; since: string; rows: Array<{ day: string; kind: string; key: string; value: number }>; accounts: number; characters: number; bankTotal: number; online: { total: number } }
const n = (v: number) => Math.round(v).toLocaleString('pt-BR');

export function Balance() {
  const [days, setDays] = useState(7);
  const [tab, setTab] = useState<'hunts' | 'eco' | 'boss' | 'deaths'>('hunts');
  const [d, setD] = useState<Data | null>(null);
  const [err, setErr] = useState('');
  useEffect(() => {
    setD(null); setErr('');
    fetch(`/api/admin/balance?days=${days}`, { headers: { authorization: `Bearer ${session.token}` }, cache: 'no-store' })
      .then(async (r) => { const j = await r.json(); if (!r.ok) throw new Error(j.error ?? 'Erro'); setD(j); }).catch((e) => setErr(e.message));
  }, [days]);
  const sum = (kind: string) => {
    const m = new Map<string, number>();
    for (const r of d?.rows ?? []) if (r.kind === kind) m.set(r.key, (m.get(r.key) ?? 0) + r.value);
    return m;
  };
  const hunts = (() => {
    const xp = sum('hunt_xp'), gold = sum('hunt_gold'), sec = sum('hunt_sec'), kills = sum('hunt_kills'), deaths = sum('deaths');
    return [...new Set([...xp.keys(), ...sec.keys()])].map((h) => {
      const hours = (sec.get(h) ?? 0) / 3600;
      return { h, hours, xph: hours > 0.05 ? (xp.get(h) ?? 0) / hours : 0, gph: hours > 0.05 ? (gold.get(h) ?? 0) / hours : 0, kills: kills.get(h) ?? 0, deaths: deaths.get(h) ?? 0 };
    }).sort((a, b) => b.hours - a.hours);
  })();
  const gin = [...sum('gold_in')].sort((a, b) => b[1] - a[1]), gout = [...sum('gold_out')].sort((a, b) => b[1] - a[1]);
  const tin = gin.reduce((t, [, v]) => t + v, 0), tout = gout.reduce((t, [, v]) => t + v, 0);
  const bossE = sum('boss_enter'), bossK = sum('boss_kill');
  const bosses = [...bossE.keys()].map((b) => ({ b, e: bossE.get(b) ?? 0, k: bossK.get(b) ?? 0 })).sort((a, b) => b.e - a.e);
  const deaths = [...sum('deaths')].sort((a, b) => b[1] - a[1]);
  return (
    <div class="bal">
      <div class="row bal-top">
        {(['hunts', 'eco', 'boss', 'deaths'] as const).map((t) => <button key={t} class={'tb-button' + (tab === t ? ' green' : '')} onClick={() => setTab(t)}>{{ hunts: 'Hunts', eco: 'Economia', boss: 'Bosses', deaths: 'Mortes' }[t]}</button>)}
        <select value={days} onChange={(e) => setDays(Number((e.target as HTMLSelectElement).value))}>
          {[1, 7, 30].map((x) => <option key={x} value={x}>{x === 1 ? 'Hoje' : `Últimos ${x} dias`}</option>)}
        </select>
      </div>
      {err && <p class="error">{err}</p>}
      {!d && !err && <p class="muted">Carregando...</p>}
      {d && <p class="small muted">Contas: {n(d.accounts)} · personagens: {n(d.characters)} · online: {d.online.total} · gold nos bancos: {n(d.bankTotal)} gp · desde {d.since.split('-').reverse().join('/')}</p>}
      {d && tab === 'hunts' && (
        <table class="bal-table"><thead><tr><th>Hunt</th><th>Horas de caça</th><th>XP/h (base)</th><th>Gold/h (loot)</th><th>Abates</th><th>Mortes</th></tr></thead>
          <tbody>{hunts.slice(0, 200).map((r) => <tr key={r.h}><td>{r.h}</td><td>{r.hours.toFixed(1)}</td><td>{n(r.xph)}</td><td>{n(r.gph)}</td><td>{n(r.kills)}</td><td>{r.deaths}</td></tr>)}
            {!hunts.length && <tr><td colSpan={6} class="muted">Sem dados ainda.</td></tr>}</tbody></table>
      )}
      {d && tab === 'eco' && (
        <div class="bal-eco">
          <p>Entrou: <b class="ok">{n(tin)} gp</b> · Saiu: <b class="error">{n(tout)} gp</b> · Saldo: <b>{n(tin - tout)} gp</b> {tin > 0 && <span class="muted">({Math.round(tout / tin * 100)}% do que entra é gasto)</span>}</p>
          <div class="row" style={{ alignItems: 'flex-start', gap: '16px' }}>
            <table class="bal-table"><thead><tr><th>Entrada</th><th>gp</th></tr></thead><tbody>{gin.map(([k, v]) => <tr key={k}><td>{k}</td><td>{n(v)}</td></tr>)}</tbody></table>
            <table class="bal-table"><thead><tr><th>Saída</th><th>gp</th></tr></thead><tbody>{gout.map(([k, v]) => <tr key={k}><td>{k}</td><td>{n(v)}</td></tr>)}</tbody></table>
          </div>
        </div>
      )}
      {d && tab === 'boss' && (
        <table class="bal-table"><thead><tr><th>Boss</th><th>Entradas</th><th>Vitórias</th><th>Taxa</th></tr></thead>
          <tbody>{bosses.map((r) => <tr key={r.b}><td>{r.b}</td><td>{r.e}</td><td>{r.k}</td><td class={r.e >= 5 && r.k / r.e < 0.3 ? 'error' : ''}>{Math.round(r.k / Math.max(1, r.e) * 100)}%</td></tr>)}
            {!bosses.length && <tr><td colSpan={4} class="muted">Sem dados ainda.</td></tr>}</tbody></table>
      )}
      {d && tab === 'deaths' && (
        <table class="bal-table"><thead><tr><th>Local</th><th>Mortes</th></tr></thead>
          <tbody>{deaths.map(([k, v]) => <tr key={k}><td>{k}</td><td>{v}</td></tr>)}{!deaths.length && <tr><td colSpan={2} class="muted">Ninguém morreu.</td></tr>}</tbody></table>
      )}
      <p class="small muted">XP/h é a experiência base dos monstros por hora de caça de um personagem (sem bônus pessoais); gold/h soma moedas e o valor de NPC dos itens. Bosses com taxa abaixo de 30% aparecem em vermelho.</p>
    </div>
  );
}
